// Shared four-plan billing model for the WebPortal (mirror of Forge's
// `agent/lib/plans.js` / `src/orchestrator/plans.ts`, billing-plans-spec).
//
// The WebPortal is a read-layer over Forge's `dev-agent-state` table: the
// authoritative `plan` lives on the `PROJECT#<id> SK=BILLING` row. This module
// carries the capability matrix the portal needs to (a) display the plan and
// its limits, and (b) gate UI options (provider dropdowns, stack pickers, etc.)
// so the portal never lets a user configure something their plan forbids.
//
// KEEP IN SYNC with Forge's plans modules.

import type { ProviderId } from './projects';

export type ProjectPlan = 'starter' | 'team' | 'business' | 'enterprise';
export type LegacyTier = 'team' | 'business' | 'enterprise';
export type MultiModelMode = 'none' | 'partial' | 'full';
export type PlanStatus = 'active' | 'suspended';

export const PLAN_ORDER: ProjectPlan[] = ['starter', 'team', 'business', 'enterprise'];
export const DEFAULT_PLAN: ProjectPlan = 'starter';
export const UNLIMITED = -1;

// FORGE-262: `starter` is a fallback-only SKU — it stays in the matrix/type as the
// no-BILLING-row default and for legacy rows, but it is NOT sold. Selection surfaces
// (signup, create-project, plan-switcher upgrade options) iterate SELLABLE_PLANS so
// starter is never offered; display surfaces still render it for existing starter
// projects (with an upgrade nudge). Do not filter starter out of PLAN_ORDER, the
// type, or the display maps.
export const SELLABLE_PLANS: ProjectPlan[] = PLAN_ORDER.filter((p) => p !== 'starter');

export interface PlanLimits {
  /** Account-level: how many projects the account may own (-1 = unlimited). */
  maxProjects: number;
  /** Per-project: repos connected to a single project (-1 = unlimited). */
  maxConnectedRepos: number;
  maxOrgs: number;
  maxConcurrentStoriesPerRepo: number;
  /** Per-agent iteration budget — applied default and ceiling (larger plans get more). */
  maxIterations: number;
  auditRetentionDays: number;
  concurrentEpicsPerRepo: number;
  additionalRepoOverageUsd: number | null;
  /** Max MCP servers a repo may configure (-1 = unlimited). Starter: 0, Team: 2, Business: 5, Enterprise: unlimited. */
  maxMcpServers: number;
  /** FORGE-262: monthly epic-unit quota (-1 = unlimited). +1/epic, +2 for a Windows-bound stack. */
  includedEpicUnits: number;
  /** FORGE-262: $ per epic-unit over the included quota (null = not sold / no metered overage). */
  overageUsdPerEpicUnit: number | null;
  /** FORGE-262: default monthly managed-compute cap when no explicit budget_cap_usd (null = none / own account). */
  defaultComputeCapUsd: number | null;
}

export interface PlanAllowedStacks {
  backend: string[];
  web: string[];
  mobile: string[];
  windows: string[];
  windows_service: string[];
  database: string[];
}

export interface PlanFeatures {
  feature_test_deploy: boolean;
  feature_smoke_tests: boolean;
  feature_browser_smoke: boolean;
  feature_post_deploy_validation: boolean;
  feature_agent_log_access: boolean;
  feature_repo_skills: boolean;
  feature_mcp_servers: boolean;
  // M4 (2026-07-14): PR review tracking + auto-fix. Mirror of Forge plans.ts
  // (Team+). Portal validation (validatePrTracking) gates on these so a Starter
  // user can't silently save pr_tracking config that Forge will ignore.
  feature_pr_tracking: boolean;
  feature_pr_tracking_auto_fix: boolean;
  feature_scheduled_tasks: boolean;
  feature_security_scans: boolean;      // S-series: scan-only + findings store (Business+)
  feature_security_scans_fix: boolean;  // S-series: fix-mode + dashboard + SLAs (Enterprise)
  feature_safety_net: boolean;          // Business / Enterprise: characterization-test safety net (FORGE-275)
  feature_migration: boolean;           // Business / Enterprise: legacy .NET → .NET 8 migration epics (FORGE-276)
  feature_custom_prompts: boolean;
  feature_custom_agents: boolean;
  feature_sso_scim: boolean;
  feature_audit_export_siem: boolean;
  feature_fallback_chain: boolean;
  feature_multi_model_routing: MultiModelMode;
  feature_custom_compliance: string[];
}

export interface PlanDefinition {
  plan: ProjectPlan;
  label: string;
  legacyTier: LegacyTier;
  priceUsd: number;
  priceLabel: string;
  cadence: string;
  limits: PlanLimits;
  allowedStacks: PlanAllowedStacks;
  features: PlanFeatures;
  /** LLM providers the plan may use (BYOK gating). */
  providers: ProviderId[];
  support: string;
}

const STARTER_STACKS: PlanAllowedStacks = {
  backend: ['node', 'none'], web: ['react', 'none'], mobile: ['flutter', 'none'],
  windows: ['none'], windows_service: ['none'], database: ['none'],
};
const TEAM_STACKS = STARTER_STACKS;
// FORGE-262 (pricing): the .NET/Windows family moved enterprise → business to
// match the public pricing page. unity stays enterprise. Mirror of Forge plans.
const BUSINESS_STACKS: PlanAllowedStacks = {
  backend: ['node', 'dotnet', 'dotnet-framework', 'java', 'python', 'none'], // B2: java (Spring Boot) + python (FastAPI/Django) — Business+
  web: ['react', 'react-vite', 'nextjs', 'angular', 'blazor', 'none'],
  mobile: ['flutter', 'none'],                 // unity stays enterprise
  windows: ['csharp', 'none'], windows_service: ['csharp', 'none'], database: ['ssdt', 'none'],
};
const ENTERPRISE_STACKS: PlanAllowedStacks = {
  backend: ['node', 'dotnet', 'dotnet-framework', 'java', 'python', 'none'], // B2: java (Spring Boot) + python (FastAPI/Django)
  web: ['react', 'react-vite', 'nextjs', 'angular', 'blazor', 'none'],
  mobile: ['flutter', 'unity', 'none'], windows: ['csharp', 'none'],
  windows_service: ['csharp', 'none'], database: ['ssdt', 'none'],
};

function featuresFor(plan: ProjectPlan): PlanFeatures {
  const teamPlus = plan === 'team' || plan === 'business' || plan === 'enterprise';
  const bizPlus = plan === 'business' || plan === 'enterprise';
  const ent = plan === 'enterprise';
  return {
    feature_test_deploy: bizPlus,
    feature_smoke_tests: bizPlus,
    feature_browser_smoke: bizPlus,
    feature_post_deploy_validation: bizPlus,
    feature_agent_log_access: ent,
    feature_repo_skills: teamPlus,
    feature_mcp_servers: teamPlus,
    feature_pr_tracking: teamPlus,           // M4: Team+ (mirror of Forge)
    feature_pr_tracking_auto_fix: teamPlus,  // M4: Team+ (mirror of Forge)
    feature_scheduled_tasks: bizPlus,
    feature_security_scans: bizPlus,
    feature_security_scans_fix: ent,
    feature_safety_net: bizPlus,
    feature_migration: bizPlus,
    feature_custom_prompts: ent,
    feature_custom_agents: ent,
    feature_sso_scim: ent,
    feature_audit_export_siem: ent,
    feature_fallback_chain: ent,
    feature_multi_model_routing: ent ? 'full' : plan === 'business' ? 'partial' : 'none',
    feature_custom_compliance: ent
      ? ['hipaa', 'hitrust', 'soc2', 'fedramp', 'pci']
      : plan === 'business' ? ['hipaa', 'hitrust'] : [],
  };
}

function providersFor(plan: ProjectPlan): ProviderId[] {
  if (plan === 'enterprise') return ['anthropic', 'bedrock', 'openai', 'vertex', 'meta', 'kimi'];
  if (plan === 'business') return ['anthropic', 'bedrock'];
  return ['anthropic']; // starter + team
}

export const PLANS: Record<ProjectPlan, PlanDefinition> = {
  starter: {
    plan: 'starter', label: 'Starter', legacyTier: 'team', priceUsd: 250, priceLabel: '$250/mo', cadence: 'monthly_or_annual',
    limits: { maxProjects: 1, maxConnectedRepos: 1, maxOrgs: 1, maxConcurrentStoriesPerRepo: 3, maxIterations: 30, auditRetentionDays: 30, concurrentEpicsPerRepo: 1, additionalRepoOverageUsd: null, maxMcpServers: 0, includedEpicUnits: 3, overageUsdPerEpicUnit: null, defaultComputeCapUsd: 50 },
    allowedStacks: STARTER_STACKS, features: featuresFor('starter'), providers: providersFor('starter'), support: 'Community',
  },
  team: {
    plan: 'team', label: 'Team', legacyTier: 'team', priceUsd: 1000, priceLabel: '$1,000/mo', cadence: 'monthly_or_annual',
    limits: { maxProjects: 3, maxConnectedRepos: 3, maxOrgs: 1, maxConcurrentStoriesPerRepo: 3, maxIterations: 40, auditRetentionDays: 90, concurrentEpicsPerRepo: 2, additionalRepoOverageUsd: null, maxMcpServers: 2, includedEpicUnits: 20, overageUsdPerEpicUnit: 40, defaultComputeCapUsd: 300 },
    allowedStacks: TEAM_STACKS, features: featuresFor('team'), providers: providersFor('team'), support: 'Email · 1 business day',
  },
  business: {
    plan: 'business', label: 'Business', legacyTier: 'business', priceUsd: 4000, priceLabel: '$4,000/mo', cadence: 'monthly_or_annual',
    limits: { maxProjects: 10, maxConnectedRepos: 15, maxOrgs: UNLIMITED, maxConcurrentStoriesPerRepo: 8, maxIterations: 75, auditRetentionDays: UNLIMITED, concurrentEpicsPerRepo: 5, additionalRepoOverageUsd: 150, maxMcpServers: 5, includedEpicUnits: 75, overageUsdPerEpicUnit: 30, defaultComputeCapUsd: 1500 },
    allowedStacks: BUSINESS_STACKS, features: featuresFor('business'), providers: providersFor('business'), support: 'Slack · 4 business hours',
  },
  enterprise: {
    plan: 'enterprise', label: 'Enterprise', legacyTier: 'enterprise', priceUsd: 120000, priceLabel: 'from $120K/yr', cadence: 'annual',
    limits: { maxProjects: UNLIMITED, maxConnectedRepos: UNLIMITED, maxOrgs: UNLIMITED, maxConcurrentStoriesPerRepo: UNLIMITED, maxIterations: 120, auditRetentionDays: UNLIMITED, concurrentEpicsPerRepo: UNLIMITED, additionalRepoOverageUsd: 0, maxMcpServers: UNLIMITED, includedEpicUnits: UNLIMITED, overageUsdPerEpicUnit: null, defaultComputeCapUsd: null },
    allowedStacks: ENTERPRISE_STACKS, features: featuresFor('enterprise'), providers: providersFor('enterprise'), support: 'Dedicated Slack + CSM · 1h P0',
  },
};

export function normalizePlan(raw: unknown): ProjectPlan {
  const s = String(raw ?? '').toLowerCase().trim();
  return (PLAN_ORDER as string[]).includes(s) ? (s as ProjectPlan) : DEFAULT_PLAN;
}

export function planToTier(plan: unknown): LegacyTier {
  return PLANS[normalizePlan(plan)].legacyTier;
}

export function tierToPlan(tier: unknown): ProjectPlan {
  const s = String(tier ?? '').toLowerCase().trim();
  if (s === 'team' || s === 'business' || s === 'enterprise') return s as ProjectPlan;
  return DEFAULT_PLAN;
}

/** Resolve the canonical plan from a BILLING/META row that may carry `plan` and/or legacy `tier`. */
export function resolvePlan(row: { plan?: string | null; tier?: string | null } | null | undefined): ProjectPlan {
  if (row && row.plan) return normalizePlan(row.plan);
  if (row && row.tier) return tierToPlan(row.tier);
  return DEFAULT_PLAN;
}

export function getPlanDef(plan: unknown): PlanDefinition {
  return PLANS[normalizePlan(plan)];
}

export function isUnlimited(v: number | null): boolean {
  return v === UNLIMITED || v === null;
}

export function planRank(plan: unknown): number {
  return PLAN_ORDER.indexOf(normalizePlan(plan));
}

export function nextPlanUp(plan: unknown): ProjectPlan | null {
  const i = planRank(plan);
  return i >= 0 && i < PLAN_ORDER.length - 1 ? PLAN_ORDER[i + 1] : null;
}

/** Human display of a limit value (∞ for unlimited). */
export function limitLabel(v: number | null): string {
  return isUnlimited(v) ? 'Unlimited' : String(v);
}
